<script setup lang="ts">
import IcClose from '@/components/Icons/IcClose.vue'
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

interface AdvancedSettings {
  resolution: string
  aggregation: string
}

interface Props {
  modelValue: boolean
  settings: AdvancedSettings
}

interface Emits {
  (e: 'update:modelValue', value: boolean): void
  (e: 'update:settings', value: AdvancedSettings): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const resolutionOptions = [
  { value: '5s', label: '5 segundos' },
  { value: '10s', label: '10 segundos' },
  { value: '30s', label: '30 segundos' },
  { value: '1m', label: '1 minuto' },
  { value: '5m', label: '5 minutos' },
  { value: '10m', label: '10 minutos' },
  { value: '15m', label: '15 minutos' },
]

const aggregationOptions = [
  { value: 'last', label: 'Último valor' },
  { value: 'mean', label: 'Média' },
  { value: 'median', label: 'Mediana' },
]

const localSettings = ref<AdvancedSettings>({
  resolution: props.settings.resolution || '30s',
  aggregation: props.settings.aggregation || 'mean',
})

watch(
  () => props.settings,
  (newSettings) => {
    localSettings.value = {
      resolution: newSettings.resolution || '30s',
      aggregation: newSettings.aggregation || 'mean',
    }
  },
  { deep: true, immediate: true },
)

const closeModal = () => {
  emit('update:modelValue', false)
}

const saveSettings = () => {
  // Garantir valores primitivos para a store persistir e a request usar o valor correto
  emit('update:settings', {
    resolution: String(localSettings.value.resolution || '30s').trim(),
    aggregation: String(localSettings.value.aggregation || 'mean').trim(),
  })
  closeModal()
}
</script>

<template>
  <Teleport to="body">
    <div v-if="modelValue" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div class="relative w-full max-w-md bg-white p-6 shadow-xl">
      <!-- Header -->
      <div class="mb-4 flex items-center justify-between">
        <div class="flex items-center">
          <h3 class="text-lg font-semibold text-gray-900">
            {{ t('registers.advancedSettings.title') }}
          </h3>
        </div>
        <button @click="closeModal" class="p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600">
          <IcClose class="h-5 w-5" />
        </button>
      </div>

      <!-- Content -->
      <div class="space-y-4">
        <!-- Resolução -->
        <div>
          <label class="mb-2 block text-sm font-medium text-gray-700">
            {{ t('registers.advancedSettings.resolution') }}
          </label>
          <select
            v-model="localSettings.resolution"
            class="w-full border border-gray-300 px-3 py-2 text-sm focus:border-blue focus:outline-none focus:ring-1 focus:ring-blue">
            <option v-for="option in resolutionOptions" :key="option.value" :value="option.value">
              {{ option.label }}
            </option>
          </select>
        </div>

        <!-- Agregação -->
        <div>
          <label class="mb-2 block text-sm font-medium text-gray-700">
            {{ t('registers.advancedSettings.aggregation') }}
          </label>
          <select
            v-model="localSettings.aggregation"
            class="w-full border border-gray-300 px-3 py-2 text-sm focus:border-blue focus:outline-none focus:ring-1 focus:ring-blue">
            <option v-for="option in aggregationOptions" :key="option.value" :value="option.value">
              {{ option.label }}
            </option>
          </select>
        </div>
      </div>

      <!-- Footer -->
      <div class="mt-6 flex justify-end">
        <div class="flex space-x-3">
          <button @click="closeModal" class="border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
            {{ t('common.cancel') }}
          </button>
          <button @click="saveSettings" class="bg-blue px-4 py-2 text-sm font-medium text-white hover:bg-blue-2">
            {{ t('common.save') }}
          </button>
        </div>
      </div>
      </div>
    </div>
  </Teleport>
</template>
